/**
 * Audit Log — security-relevant action history.
 *
 * Operator's "who did what when" view. Filters fold inline above the
 * table (denser than a side panel, fits the operational aesthetic).
 *
 * Pagination is offset+limit. `has_more` from the server drives the
 * Next button; we don't precompute total count to keep queries cheap
 * on big tables.
 */

import { useCallback, useMemo, useState } from "react";
import { Download, Filter, RefreshCw, Search } from "lucide-react";
import { Badge, Button, Card, CardHeader, Input } from "@vseh/ui";

import {
  auditApi, observabilityApi,
  type AuditLogFilters, type AuditLogPage, type AuditLogRow, type AuditRowDto,
} from "../shared/api";
import { useFetch } from "../shared/hooks";
import { useT } from "../shared/i18n";
import { ErrorBlock } from "../shared/components/States";
import { SkeletonTable } from "../shared/components/Skeleton";


const PAGE_SIZE = 50;


/** Translate the old paginated filters into the Sprint BD `/audit`
 *  shape (different field names). Kept inline so the rest of the
 *  page doesn't change once we switch endpoints. */
function _toSearchFilters(
  f: AuditLogFilters,
  q: string,
): import("../shared/api").AuditSearchFilters {
  const out: import("../shared/api").AuditSearchFilters = {};
  if (f.actor_id)    out.actor       = f.actor_id;
  if (f.action)      out.action      = f.action;
  if (f.target_type) out.target_type = f.target_type;
  if (f.since)       out.from        = f.since;
  if (f.until)       out.to          = f.until;
  if (q)             out.q           = q;
  return out;
}


/** Adapt the flat-list Sprint BD response into the paginated shape
 *  the rest of the page already consumes. `has_more` flips when we
 *  hit the server's 1000-row ceiling. */
function _adaptSearchResponse(
  rows: AuditRowDto[],
  pageSize: number,
): AuditLogPage {
  return {
    rows:   rows.slice(0, pageSize) as unknown as AuditLogRow[],
    offset: 0,
    limit:  pageSize,
    has_more: rows.length > pageSize,
  };
}


export function AuditLogPage(): JSX.Element {
  const t = useT();
  const [filters, setFilters] = useState<AuditLogFilters>({
    limit: PAGE_SIZE, offset: 0,
  });
  // Sprint BN — `q` substring lives in its own state. The old
  // /observability/audit-log endpoint doesn't support it, so when
  // q is set we switch to the Sprint BD /audit endpoint that does.
  const [q, setQ] = useState<string>("");
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvErr,  setCsvErr]  = useState<string | null>(null);

  const useSearch = q.trim() !== "";

  const fetcher = useCallback(
    async (signal: AbortSignal): Promise<AuditLogPage> => {
      if (useSearch) {
        const rows = await auditApi.search(
          { ..._toSearchFilters(filters, q), limit: PAGE_SIZE + 1 },
          { signal },
        );
        return _adaptSearchResponse(rows, PAGE_SIZE);
      }
      return observabilityApi.auditLog(filters, { signal });
    },
    [filters, q, useSearch],
  );
  const { data, error, loading, refetch } = useFetch<AuditLogPage>(
    fetcher, [filters, q, useSearch],
  );

  const downloadCsv = useCallback(async () => {
    setCsvBusy(true);
    setCsvErr(null);
    try {
      const stamp = new Date().toISOString().slice(0, 10);
      await auditApi.downloadCsv(
        _toSearchFilters(filters, q),
        `audit_log_${stamp}.csv`,
      );
    } catch (e) {
      setCsvErr(e instanceof Error ? e.message : String(e));
    } finally {
      setCsvBusy(false);
    }
  }, [filters, q]);

  // Pinned for the CSV-disabled tooltip; the export endpoint is
  // safe for any filter set but a multi-million-row export from an
  // unfiltered query is a CDN-grade payload we'd rather discourage.
  const hasAnyFilter = useMemo(
    () => Boolean(
      q.trim() || filters.actor_id || filters.action ||
      filters.target_type || filters.since || filters.until,
    ),
    [q, filters],
  );

  const setFilter = useCallback(
    (key: keyof AuditLogFilters, value: string | undefined) => {
      setFilters((prev) => {
        const next = { ...prev, offset: 0 };
        if (value === undefined || value === "") {
          delete (next as Record<string, unknown>)[key];
        } else {
          (next as Record<string, unknown>)[key] = value;
        }
        return next;
      });
    },
    [],
  );

  const nextPage = useCallback(() => {
    setFilters((prev) => ({ ...prev, offset: (prev.offset ?? 0) + PAGE_SIZE }));
  }, []);
  const prevPage = useCallback(() => {
    setFilters((prev) => ({
      ...prev,
      offset: Math.max(0, (prev.offset ?? 0) - PAGE_SIZE),
    }));
  }, []);

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader
          title={t("audit.page.title")}
          description={t("audit.page.subtitle")}
          actions={
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="xs"
                      leftIcon={<RefreshCw className="size-3.5" />}
                      onClick={refetch}>
                {t("common.refresh")}
              </Button>
              <Button
                variant="ghost" size="xs"
                leftIcon={<Download className="size-3.5" />}
                disabled={csvBusy || !hasAnyFilter}
                onClick={downloadCsv}
                title={
                  hasAnyFilter
                    ? t("audit.csv.tooltip.enabled")
                    : t("audit.csv.tooltip.disabled")
                }
              >
                {csvBusy
                  ? t("audit.action.preparing_csv")
                  : t("audit.action.download_csv")}
              </Button>
            </div>
          }
        />

        {csvErr && (
          <div className="mb-3 rounded-sm border border-danger-200 bg-danger-50 px-3 py-2 text-xs text-danger-700">
            {csvErr}
          </div>
        )}

        {/* Sprint BN — substring search over actor / action /
            target_id, served by the /audit endpoint. When set,
            we switch the data path off the paginated endpoint
            (which doesn't support `q`) onto the flat-list one. */}
        <div className="mb-3 flex items-center gap-2">
          <Search className="size-3.5 text-base-500" />
          <Input
            placeholder={t("audit.search.placeholder")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          {useSearch && (
            <Button
              variant="ghost" size="xs"
              onClick={() => setQ("")}
            >
              {t("audit.search.reset")}
            </Button>
          )}
        </div>

        <FilterRow filters={filters} onSet={setFilter} />

        {loading && !data && <SkeletonTable rows={8} cols={6} />}
        {error && <ErrorBlock error={error} onRetry={refetch} />}

        {data && data.rows.length === 0 && (
          <div className="py-8 text-center text-sm text-base-500">
            {t("audit.empty")}
          </div>
        )}

        {data && data.rows.length > 0 && <AuditTable rows={data.rows} />}

        {data && !useSearch && (
          <Pagination
            offset={data.offset}
            limit={data.limit}
            hasMore={data.has_more}
            onPrev={prevPage}
            onNext={nextPage}
          />
        )}
        {data && useSearch && data.has_more && (
          <div className="mt-3 border-t border-base-200 pt-2 text-2xs text-base-500">
            {t("audit.truncated", { limit: PAGE_SIZE })}
          </div>
        )}
      </Card>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Filter row
// ---------------------------------------------------------------------------


function FilterRow(props: {
  filters: AuditLogFilters;
  onSet: (key: keyof AuditLogFilters, value: string | undefined) => void;
}): JSX.Element {
  return (
    <div className="mb-3 grid grid-cols-12 gap-2">
      <div className="col-span-3">
        <Input
          placeholder="actor_id"
          value={props.filters.actor_id ?? ""}
          onChange={(e) => props.onSet("actor_id", e.target.value)}
          prefix={<Filter className="size-3.5" />}
        />
      </div>
      <div className="col-span-3">
        <Input
          placeholder="action (e.g. agent.execute)"
          value={props.filters.action ?? ""}
          onChange={(e) => props.onSet("action", e.target.value)}
          prefix={<Filter className="size-3.5" />}
        />
      </div>
      <div className="col-span-2">
        <Input
          placeholder="target_type"
          value={props.filters.target_type ?? ""}
          onChange={(e) => props.onSet("target_type", e.target.value)}
        />
      </div>
      <div className="col-span-2">
        <Input
          type="date"
          value={props.filters.since?.split("T")[0] ?? ""}
          onChange={(e) =>
            props.onSet("since", e.target.value
              ? `${e.target.value}T00:00:00`
              : undefined)
          }
        />
      </div>
      <div className="col-span-2">
        <Input
          type="date"
          value={props.filters.until?.split("T")[0] ?? ""}
          onChange={(e) =>
            props.onSet("until", e.target.value
              ? `${e.target.value}T23:59:59`
              : undefined)
          }
        />
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------


function AuditTable({ rows }: { rows: AuditLogRow[] }): JSX.Element {
  const t = useT();
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="border-b border-base-200 text-2xs uppercase tracking-wide text-base-500">
          <tr>
            <th className="py-1.5 pr-3 text-left font-medium">{t("audit.col.when")}</th>
            <th className="py-1.5 pr-3 text-left font-medium">{t("audit.col.actor")}</th>
            <th className="py-1.5 pr-3 text-left font-medium">{t("audit.col.action")}</th>
            <th className="py-1.5 pr-3 text-left font-medium">{t("audit.col.target")}</th>
            <th className="py-1.5 pr-3 text-left font-medium">{t("audit.col.result")}</th>
            <th className="py-1.5 pr-3 text-left font-medium">{t("audit.col.ip")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <AuditTableRow key={r.id} row={r} />
          ))}
        </tbody>
      </table>
    </div>
  );
}


function AuditTableRow({ row }: { row: AuditLogRow }): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const hasDetails = Object.keys(row.details).length > 0;
  return (
    <>
      <tr
        className="border-b border-base-100 align-top hover:bg-base-50 cursor-pointer"
        onClick={() => hasDetails && setExpanded(!expanded)}
      >
        <td className="py-1.5 pr-3 text-base-700 tabular-nums">
          {new Date(row.occurred_at).toLocaleString("ru-RU")}
        </td>
        <td className="py-1.5 pr-3">
          <div className="flex items-center gap-1.5">
            <ActorBadge actorType={row.actor_type} />
            <span className="truncate font-mono text-base-800">
              {row.actor_id.slice(0, 12)}
            </span>
          </div>
        </td>
        <td className="py-1.5 pr-3 font-mono text-base-900">{row.action}</td>
        <td className="py-1.5 pr-3 text-base-700">
          {row.target_type ? (
            <>
              <span className="text-base-500">{row.target_type}/</span>
              <span className="font-mono">{(row.target_id ?? "").slice(0, 12)}</span>
            </>
          ) : (
            <span className="text-base-400">—</span>
          )}
        </td>
        <td className="py-1.5 pr-3">
          <ResultBadge result={row.result} />
        </td>
        <td className="py-1.5 pr-3 font-mono text-base-600">
          {row.client_ip ?? "—"}
        </td>
      </tr>
      {expanded && hasDetails && (
        <tr>
          <td colSpan={6} className="bg-base-50 px-3 py-2">
            <pre className="overflow-x-auto text-2xs text-base-700">
              {JSON.stringify(row.details, null, 2)}
            </pre>
          </td>
        </tr>
      )}
    </>
  );
}


// ---------------------------------------------------------------------------
// Presentation helpers
// ---------------------------------------------------------------------------


function ActorBadge({ actorType }: { actorType: string }): JSX.Element {
  const tone = {
    human:     "ai"      as const,
    service:   "neutral" as const,
    agent:     "success" as const,
    connector: "neutral" as const,
  }[actorType] ?? "neutral";
  return <Badge tone={tone}>{actorType}</Badge>;
}


function ResultBadge({ result }: { result: string }): JSX.Element {
  const tone = {
    success: "success" as const,
    denied:  "danger"  as const,
    failed:  "danger"  as const,
  }[result] ?? "neutral";
  return <Badge tone={tone} dot>{result}</Badge>;
}


function Pagination(props: {
  offset: number;
  limit:  number;
  hasMore: boolean;
  onPrev: () => void;
  onNext: () => void;
}): JSX.Element {
  const t = useT();
  return (
    <div className="mt-3 flex items-center justify-between border-t border-base-200 pt-2 text-2xs text-base-500">
      <span className="tabular-nums">
        {props.offset + 1}–{props.offset + props.limit}
      </span>
      <div className="flex items-center gap-1">
        <Button
          variant="ghost" size="xs"
          disabled={props.offset === 0}
          onClick={props.onPrev}
        >
          {t("pager.prev")}
        </Button>
        <Button
          variant="ghost" size="xs"
          disabled={!props.hasMore}
          onClick={props.onNext}
        >
          {t("pager.next")}
        </Button>
      </div>
    </div>
  );
}
